import { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Autocomplete,
  Button,
  Alert,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import DetailSidebar from '../../../shared/components/DetailSidebar';
import FormActionBar from '../../../shared/components/FormActionBar';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import SubscribePreviewPanel from './SubscribePreviewPanel';
import { useSubscribeFlow } from '../hooks/useSubscribeFlow';
import { useCatalogService } from '../services/catalogService';
import type { ServiceComponentSummary } from '../services/catalogService';
import { DeploymentStatus } from '../../../types/backend';
import type { AwsAccount, Deployment } from '../../../types/backend';

export interface SubscribeAccountOption {
  accountId: string;
  label: string;
  defaultRegion: string;
  supportedRegions: string[];
}

interface SubscribePanelProps {
  customerId: string;
  awsAccounts: AwsAccount[];
  products: ServiceComponentSummary[];
  onCancel: () => void;
  onSubscribed: () => void;
}

// Subscription only ever targets workload accounts. The operations account was a
// Service Catalog provisioning target historically; those products were
// decommissioned (see backend migrate_remove_service_component_account_type), so
// it is no longer offered here and is rejected fail-closed by the backend.
function buildAccountOptions(awsAccounts: AwsAccount[]): SubscribeAccountOption[] {
  return awsAccounts.map((acc) => {
    const fallbackRegion = acc.main_region ?? '';
    const supportedRegions = acc.supported_regions ?? [];
    return {
      accountId: acc.aws_account_id,
      label: acc.account_name || 'Workload account',
      defaultRegion: fallbackRegion,
      supportedRegions: supportedRegions.length > 0
        ? supportedRegions
        : (fallbackRegion ? [fallbackRegion] : []),
    };
  });
}

const SubscribePanel = memo<SubscribePanelProps>(({
  customerId,
  awsAccounts,
  products,
  onCancel,
  onSubscribed,
}) => {
  const flow = useSubscribeFlow({ customerId, onSubscribed });

  const catalogService = useCatalogService();
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [deploymentsError, setDeploymentsError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    catalogService.listDeployments(customerId).then(
      (resp) => { if (!cancelled) setDeployments(resp.deployments ?? []); },
      (err) => { if (!cancelled) setDeploymentsError(err instanceof Error ? err.message : 'Failed to load existing deployments'); },
    );
    return () => { cancelled = true; };
  }, [customerId, catalogService]);

  const accountOptions = useMemo(
    () => buildAccountOptions(awsAccounts),
    [awsAccounts],
  );

  const alreadySubscribedServices = useMemo(() => {
    if (!flow.form.accountId) return [] as string[];
    const services = new Set<string>();
    deployments.forEach((d) => {
      // Only an actively-managed (desired=DEPLOYED) row counts as a live subscription.
      // TERMINATED (destroyed) and UNSUBSCRIBED (still running, OpsPilot no longer
      // manages it) are both inactive for dedup purposes — re-subscribing is valid.
      if (d.account_id === flow.form.accountId && d.desired_status === DeploymentStatus.DEPLOYED) {
        services.add(d.service);
      }
    });
    return Array.from(services).sort();
  }, [deployments, flow.form.accountId]);

  const availableServices = useMemo(() => {
    if (!flow.form.accountId) return [];
    const set = new Set<string>();
    products.forEach((p) => set.add(p.service));
    return Array.from(set).sort();
  }, [products, flow.form.accountId]);

  // Optional (opt-in) products for the selected services: those NOT auto-deployed.
  const optionalServiceComponents = useMemo(
    () => products.filter((p) => flow.form.services.includes(p.service) && !p.auto_deploy),
    [products, flow.form.services],
  );

  const selectedAccount = useMemo(
    () => accountOptions.find((a) => a.accountId === flow.form.accountId) ?? null,
    [accountOptions, flow.form.accountId],
  );

  // Auto-preview whenever the account + services selection changes, so the
  // operator never has to hunt for a separate "Preview" button. Keyed on the
  // selection only (not on canPreview, which flips while loading and would loop).
  useEffect(() => {
    if (flow.form.accountId && flow.form.services.length > 0) {
      flow.runPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.form.accountId, flow.form.services]);

  const handleAccountChange = (accountId: string) => {
    const account = accountOptions.find((a) => a.accountId === accountId);
    if (!account) return;
    flow.dispatch({ type: 'set_account', accountId: account.accountId });
    flow.dispatch({ type: 'set_services', services: [] });
    flow.dispatch({ type: 'set_regions', regions: [account.defaultRegion] });
  };

  // Drop opted-in add-ons that are no longer available for the current services.
  useEffect(() => {
    const valid = new Set(optionalServiceComponents.map((p) => p.name));
    const pruned = flow.form.includeServiceComponents.filter((n) => valid.has(n));
    if (pruned.length !== flow.form.includeServiceComponents.length) {
      flow.dispatch({ type: 'set_include_products', includeServiceComponents: pruned });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionalServiceComponents]);

  const toggleOptionalServiceComponent = (name: string, checked: boolean) => {
    const next = checked
      ? [...flow.form.includeServiceComponents, name]
      : flow.form.includeServiceComponents.filter((n) => n !== name);
    flow.dispatch({ type: 'set_include_products', includeServiceComponents: next });
  };

  return (
    <DetailSidebar title="Subscribe a component" onClose={onCancel}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {accountOptions.length === 0 ? (
            <Alert severity="warning">
              This customer has no workload accounts registered. Add an AWS account before subscribing.
            </Alert>
          ) : (
            <TextField
              select
              size="small"
              fullWidth
              label="Account"
              value={flow.form.accountId}
              onChange={(e) => handleAccountChange(e.target.value)}
              helperText="The account these services are deployed into."
            >
              {accountOptions.map((opt) => (
                <MenuItem key={opt.accountId} value={opt.accountId}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="body2">{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {opt.accountId}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box>
            <Autocomplete
              multiple
              size="small"
              options={availableServices}
              value={flow.form.services}
              onChange={(_e, value) => flow.dispatch({ type: 'set_services', services: value })}
              disabled={!flow.form.accountId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Services"
                  placeholder={
                    !flow.form.accountId
                      ? 'Select an account first'
                      : availableServices.length === 0
                        ? 'No services available'
                        : 'Pick one or more services'
                  }
                />
              )}
            />
            {flow.form.accountId && alreadySubscribedServices.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">Already subscribed:</Typography>
                {alreadySubscribedServices.map((svc) => (
                  <Chip key={svc} label={svc} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                ))}
              </Box>
            )}
            {deploymentsError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{deploymentsError}</Typography>
            )}
          </Box>

          <Autocomplete
            multiple
            size="small"
            freeSolo
            options={selectedAccount?.supportedRegions ?? []}
            value={flow.form.regions}
            onChange={(_e, value) => flow.dispatch({ type: 'set_regions', regions: value })}
            disabled={!selectedAccount}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Regions"
                placeholder={selectedAccount ? 'Defaults to the account main region' : 'Select an account first'}
              />
            )}
          />

          {optionalServiceComponents.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.25 }}>Optional add-ons</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Not deployed by default. Tick any you also want to include.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {optionalServiceComponents.map((p) => (
                  <FormControlLabel
                    key={p.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={flow.form.includeServiceComponents.includes(p.name)}
                        onChange={(e) => toggleOptionalServiceComponent(p.name, e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{p.name}</Typography>
                        {p.description && (
                          <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                        )}
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', mx: 0, py: 0.25 }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <SubscribePreviewPanel
            preview={flow.preview}
            loading={flow.previewLoading}
            error={flow.previewError}
            canPreview={flow.canPreview}
            onRunPreview={flow.runPreview}
          />
        </Box>

        <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <FormActionBar
            onSave={flow.submit}
            saving={flow.submitting}
            saveDisabled={!flow.canSubmit}
            saveLabel="Subscribe"
            savingLabel="Subscribing..."
            sx={{ mt: 0, pt: 0, borderTop: 0 }}
          >
            <Button onClick={onCancel} disabled={flow.submitting}>Cancel</Button>
          </FormActionBar>
        </Box>
      </Box>
      <SnackbarAlert snackbar={flow.snackbar} onClose={flow.closeSnackbar} />
    </DetailSidebar>
  );
});

SubscribePanel.displayName = 'SubscribePanel';
export default SubscribePanel;
